import { LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';

import { COLOR_PALETTE } from 'constants/ui';

export const DEFAULT_HISTORY_TITLE = 'Change history';

export const DEFAULT_PAGE_SIZE = 25;

export const AuditOperation = {
  CREATED: 'Created',
  UPDATED: 'Updated',
  DELETED: 'Deleted',
};

/** Wire tolerance: the host serializes enums as strings, but older/number forms still normalize. */
export const AUDIT_OPERATION_BY_WIRE = {
  1: AuditOperation.CREATED,
  2: AuditOperation.UPDATED,
  3: AuditOperation.DELETED,
  Created: AuditOperation.CREATED,
  Updated: AuditOperation.UPDATED,
  Deleted: AuditOperation.DELETED,
};

export const AUDIT_OPERATION_LABEL = {
  [AuditOperation.CREATED]: 'Created',
  [AuditOperation.UPDATED]: 'Updated',
  [AuditOperation.DELETED]: 'Deleted',
};

export const AUDIT_OPERATION_VARIANT = {
  [AuditOperation.CREATED]: LabelVariant.GREEN,
  [AuditOperation.UPDATED]: LabelVariant.BRAND,
  [AuditOperation.DELETED]: LabelVariant.RED,
};

export const ActorType = {
  HUMAN: 'Human',
  SERVICE: 'Service',
  SYSTEM: 'System',
};

export const AUDIT_ACTOR_TYPE_BY_WIRE = {
  1: ActorType.HUMAN,
  2: ActorType.SERVICE,
  3: ActorType.SYSTEM,
  Human: ActorType.HUMAN,
  Service: ActorType.SERVICE,
  System: ActorType.SYSTEM,
};

export const SYSTEM_ACTOR_LABEL = 'System';

/** Actor secondary lines: System shows its initiator, a Service account says so. */
export const TRIGGERED_BY_PREFIX = 'Triggered by';

export const SERVICE_ACCOUNT_SUBLABEL = 'Service account';

/** Create/Delete rows carry no field list (§3 row anatomy). */
export const CHANGED_FIELDS_NONE = '—';

/** A Create row has no diff; its fields cell names the single-sided snapshot instead. */
export const INITIAL_VALUES_LABEL = 'Initial values';

/** Field chips shown in the row before the rest collapse to "+N". */
export const FIELDS_PILL_LIMIT = 2;

export const TIMELINE_COLUMN_LABELS = {
  time: 'Time',
  operation: 'Change',
  actor: 'Changed by',
  changedFields: 'Fields changed',
};

/**
 * The timeline header carries per-column sort + filter (the pipelines-table pattern); these ids are
 * the sort keys and the filter column keys.
 */
export const AUDIT_COLUMN_ID = {
  EXPAND: 'expand',
  TIME: 'time',
  OPERATION: 'operation',
  ACTOR: 'actor',
  FIELDS: 'changed-fields',
  ACTIONS: 'actions',
};

/** Fixed timeline column widths; Fields changed takes the rest of the row. */
export const AUDIT_COLUMN_WIDTH = {
  EXPAND: '48px',
  TIME: '200px',
  OPERATION: '140px',
  ACTOR: '240px',
};

// Decorated rows stack scope chips (e.g. "Not effective for you") under the operation pill, which
// 140px clips. Changed by gives the width back, so Fields changed keeps the same share.
export const DECORATED_AUDIT_COLUMN_WIDTH = {
  ...AUDIT_COLUMN_WIDTH,
  OPERATION: '180px',
  ACTOR: '200px',
};

/** The mid shade used for a filter option's colour dot. */
const OPERATION_DOT_SHADE = 6;

/** Options for the Change-column filter menu — each labelled and colour-dotted by operation. */
export const OPERATION_FILTER_SELECT_OPTIONS = [
  {
    value: AuditOperation.CREATED,
    label: AUDIT_OPERATION_LABEL[AuditOperation.CREATED],
    bg: COLOR_PALETTE[LabelVariant.GREEN][OPERATION_DOT_SHADE],
  },
  {
    value: AuditOperation.UPDATED,
    label: AUDIT_OPERATION_LABEL[AuditOperation.UPDATED],
    bg: COLOR_PALETTE[LabelVariant.BRAND][OPERATION_DOT_SHADE],
  },
  {
    value: AuditOperation.DELETED,
    label: AUDIT_OPERATION_LABEL[AuditOperation.DELETED],
    bg: COLOR_PALETTE[LabelVariant.RED][OPERATION_DOT_SHADE],
  },
];

/** System rows carry no principal id, so they share one actor-filter bucket. */
export const ACTOR_FILTER_SYSTEM_KEY = '__system__';

export const getHistoryCountLabel = count => `${count} ${count === 1 ? 'change' : 'changes'}`;

export const HISTORY_LOADING_LABEL = 'Loading history…';

export const HISTORY_EMPTY_MESSAGE = 'No changes recorded for this item.';

/**
 * Shown in the table body when the loaded history is non-empty but the active column filters (e.g.
 * a Fields-changed search) match no rows — distinct from HISTORY_EMPTY_MESSAGE (nothing recorded).
 */
export const HISTORY_NO_MATCH_MESSAGE = 'No changes match the current filters.';

export const HISTORY_LOAD_FAILED_MESSAGE = 'Could not load the change history.';

export const HISTORY_RETRY_LABEL = 'Try again';

/**
 * A history read that 404s means the endpoint is not deployed on this host yet — a deployment
 * state rather than a failure worth a red toast.
 */
export const HISTORY_UNAVAILABLE_MESSAGE = "History isn't available here yet.";

export const getLoadMoreLabel = count => `Load ${count} more`;

export const getShowingCountNote = (shown, total) => `Showing ${shown} of ${total}`;

export const HISTORY_LOADING_MORE_LABEL = 'Loading…';

export const HISTORY_END_OF_LIST_MESSAGE = 'All changes shown.';

/** The whole index row is present but the drill-in payload (blob) could not be read. */
export const DETAIL_UNAVAILABLE_MESSAGE = 'Details unavailable.';

export const DETAIL_LOADING_LABEL = 'Loading details…';

export const DETAIL_RETRY_LABEL = 'Try again';

/** Shown in Was/Became when snapshotting was disabled for the type — values were suppressed. */
export const VALUE_HIDDEN_PLACEHOLDER = 'value hidden';

/** The column a field renderer draws: Was, Became, or the one Value of a create or delete. */
export const AuditValueSide = {
  OLD: 'old',
  NEW: 'new',
  SINGLE: 'single',
};

export const NO_FIELD_CHANGES_NOTE = 'No field-level changes were recorded for this entry.';

export const ON_BEHALF_OF_PREFIX = 'on behalf of';

/** The drill-in splits into a per-field diff and the full before/after snapshot. */
export const DetailTab = {
  CHANGES: 'changes',
  SNAPSHOT: 'snapshot',
};

export const DETAIL_TAB_LABELS = {
  [DetailTab.CHANGES]: 'Changes',
  [DetailTab.SNAPSHOT]: 'Snapshot',
};

/** Heads the field diff when `changePanels` take some changes out of it. */
export const FIELD_CHANGES_PANEL_TITLE = 'Field changes';

/** Marks a field that changes on every save, such as a version counter. */
export const AUTOMATIC_FIELD_NOTE = 'automatic';

export const SNAPSHOT_BEFORE_LABEL = 'Before';

export const SNAPSHOT_AFTER_LABEL = 'After';

export const COPY_ID_LABEL = 'Copy ID';

export const COPY_JSON_LABEL = 'Copy JSON';

/** The toast shown after a successful copy, and how long it stays up. */
export const COPY_ID_SUCCESS_MESSAGE = 'Audit ID copied to clipboard';

export const COPY_JSON_SUCCESS_MESSAGE = 'Snapshot JSON copied to clipboard';

export const COPY_NOTIFICATION_DURATION = 1_500;

/** The drill-in before any row is expanded; `requestKey` names the row + read the detail is for. */
export const IDLE_EXPANDED_DETAIL = {
  requestKey: null,
  isLoading: false,
  detail: null,
  isUnavailable: false,
};

/** The row a row action asked to open, and how many times it asked. */
export const IDLE_ROW_SCROLL = { rowId: null, request: 0 };

// Centred while its detail is still loading, so the row sits just above it.
export const OPENED_ROW_SCROLL_OPTIONS = { block: 'center', behavior: 'smooth' };

/** Shared default for the optional field-list props, so an omitted prop keeps memoized values stable. */
export const EMPTY_FIELD_LIST = [];

/** The read flag that asks the host for override-shadowed (not effective) rows too. */
export const INCLUDE_SHADOWED_PARAMS = { includeShadowed: true };

/** A server summariser that failed closed: the raw value is withheld and no summary data exists. */
export const AUDIT_SUMMARY_UNAVAILABLE_KIND = 'audit.summaryUnavailable';

/** Shown for a summarised change with no renderer for its kind — never the raw value. */
export const FIELD_REPLACED_LABEL = 'Field replaced';

export const CHANGES_COLUMN_LABELS = {
  field: 'Field',
  was: 'Was',
  became: 'Became',
  value: 'Value',
};

export const getFieldsDifferNote = count =>
  `${count} ${count === 1 ? 'field differs' : 'fields differ'}`;
